import React, { useState } from 'react';
import AccountingDashboard from './AccountingDashboard';
import ProspectPipeline from './ProspectPipeline';
import DevisManagement from './DevisManagement';
import UnifiedClientPipeline from './UnifiedClientPipeline';
import ClientProductAssignment from './ClientProductAssignment';
import InvoiceManagement from './InvoiceManagement';
import { useTheme } from '../contexts/ThemeContext';
import { db } from '../firebase';

/**
 * Gestionnaire principal de la comptabilité - Version Responsive
 * Fournit une navigation adaptative entre les différents modules comptables
 */

const AccountingManagement = () => {
  const [moduleActif, setModuleActif] = useState('tableau-de-bord');
  const [menuMobileOuvert, setMenuMobileOuvert] = useState(false);
  const { theme } = useTheme();

  const modules = [
    {
      id: 'tableau-de-bord',
      label: 'Tableau de bord',
      icone: '📊',
      description: 'Vue d\'ensemble des finances'
    },
    {
      id: 'pipeline-unifie',
      label: 'Pipeline prospects/Bénéficiaires',
      icone: '🔄',
      description: 'Gestion unifiée du cycle de vente'
    },
    {
      id: 'devis',
      label: 'Gestion des devis',
      icone: '📄',
      description: 'Création et suivi des devis'
    },
    {
      id: 'factures',
      label: 'Gestion des factures',
      icone: '💰',
      description: 'Facturation et suivi des paiements'
    },
    {
      id: 'attribution-produits',
      label: 'Attribution produits',
      icone: '🎯',
      description: 'Attribuer produits aux clients'
    },
    {
      id: 'rapports',
      label: 'Rapports financiers',
      icone: '📈',
      description: 'Analyses et statistiques'
    }
  ];

  const renderModule = () => {
    switch (moduleActif) {
      case 'tableau-de-bord':
        return <AccountingDashboard />;
      case 'pipeline-unifie':
        return <UnifiedClientPipeline />;
      case 'devis':
        return <DevisManagement />;
      case 'factures':
        return <InvoiceManagement />;
      case 'attribution-produits':
        return (
          <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-6">
            <div className="max-w-7xl mx-auto">
              <ClientProductAssignment db={db} />
            </div>
          </div>
        );
      case 'rapports':
        return (
          <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-6">
            <div className="max-w-7xl mx-auto">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 md:p-6">
                <h2 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white mb-4">Rapports financiers</h2>
                <p className="text-gray-600 dark:text-gray-400 text-sm md:text-base">
                  Analyses détaillées et indicateurs de performance.
                </p>
                <div className="mt-4 p-3 md:p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <p className="text-purple-800 dark:text-purple-300 text-xs md:text-sm">
                    💡 <strong>Fonctionnalités à venir :</strong> Chiffre d'affaires par période, 
                    taux de conversion, prévisions de trésorerie, export Excel.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return <AccountingDashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header mobile */}
      <div className="md:hidden bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center">
            <button
              onClick={() => setMenuMobileOuvert(!menuMobileOuvert)}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 mr-3"
              aria-label="Ouvrir le menu"
            >
              <span className="text-xl dark:text-gray-300">☰</span>
            </button>
            <div>
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">Comptabilité</h1>
              <p className="text-xs text-gray-600 dark:text-gray-400">Gestion financière</p>
            </div>
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {modules.find(m => m.id === moduleActif)?.icone}
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Navigation latérale - Desktop */}
        <div className="hidden md:block w-64 bg-white dark:bg-gray-800 shadow-sm min-h-screen">
          <div className="p-6 border-b dark:border-gray-700">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Comptabilité</h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">Gestion financière complète</p>
          </div>
          
          <nav className="p-4">
            <ul className="space-y-2">
              {modules.map((module) => (
                <li key={module.id}>
                  <button
                    onClick={() => setModuleActif(module.id)}
                    className={`w-full text-left p-3 rounded-lg transition-colors min-h-[60px] ${
                      moduleActif === module.id
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-l-4 border-blue-500 dark:border-blue-400'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <span className="text-lg mr-3">{module.icone}</span>
                      <div>
                        <div className="font-medium text-sm md:text-base">{module.label}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{module.description}</div>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* Informations système */}
          <div className="p-4 border-t dark:border-gray-700 mt-4">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              <p><strong>OMAD SERVICES</strong></p>
              <p className="mt-1">Système comptable intégré</p>
              <p>Version 1.0</p>
            </div>
          </div>
        </div>

        {/* Navigation mobile - Overlay */}
        {menuMobileOuvert && (
          <div className="md:hidden fixed inset-0 z-50 bg-black bg-opacity-50 dark:bg-opacity-70">
            <div className="fixed inset-y-0 left-0 w-64 bg-white dark:bg-gray-800 shadow-lg">
              <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Navigation</h2>
                <button
                  onClick={() => setMenuMobileOuvert(false)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700"
                  aria-label="Fermer le menu"
                >
                  <span className="text-xl dark:text-gray-300">✕</span>
                </button>
              </div>
              
              <nav className="p-4">
                <ul className="space-y-2">
                  {modules.map((module) => (
                    <li key={module.id}>
                      <button
                        onClick={() => {
                          setModuleActif(module.id);
                          setMenuMobileOuvert(false);
                        }}
                        className={`w-full text-left p-3 rounded-lg transition-colors min-h-[60px] ${
                          moduleActif === module.id
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-l-4 border-blue-500 dark:border-blue-400'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <div className="flex items-center">
                          <span className="text-lg mr-3">{module.icone}</span>
                          <div>
                            <div className="font-medium">{module.label}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{module.description}</div>
                          </div>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </div>
        )}

        {/* Contenu principal */}
        <div className="flex-1 md:ml-0">
          {renderModule()}
        </div>
      </div>
    </div>
  );
};

export default AccountingManagement;
